import { useState } from 'react';

import {
  CreatingStatusProps,
  NumberDetailWhatsAppStatusIntegrationSectionProps,
  RegisteredStatusProps,
  ValidationStatusValueProps,
} from './NumberDetailWhatsAppStatusIntegrationSection.decl';

import {
  ChevronDownOutlined,
  ChevronRightOutlined,
  InformationOutlined,
  OutboundOutlined,
  RefreshOutlined,
} from '@aircall/icons';
import {
  Icon,
  Tag,
  Typography,
  Box,
  Link,
  Flex,
  Button,
  List,
  ListView,
  StyledListItem,
  ListItem,
  getColor,
  getSpace,
  TagVariants,
  getRadii,
} from '@aircall/tractor-v2';
import { WhatsappRegistrationState, SendMessageStatus } from '@generated/globalTypes';
import styled from '@xstyled/styled-components';
import { Trans, useTranslation } from 'react-i18next';

const StyledUl = styled(Box)`
  padding-left: ${getSpace('s')};
  list-style-type: disc;

  li::marker {
    color: ${({ color }: { color: string }) => getColor(color)};
  }
`;

const StyledRegistrationInfo = styled(Box)`
  padding: ${getSpace('xxs')};
  border-radius: ${getRadii('sm')};
  margin-top: ${getSpace('s')};
  background-color: ${getColor('surface-background')};
`;

function NotRegisteredStatus({
  numberFormatted,
  wabaId,
}: {
  readonly numberFormatted: string | null | undefined;
  readonly wabaId: string | null | undefined;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  const toggleContent = () => {
    setIsOpen((prevState) => !prevState);
  };

  return (
    <Box minWidth='0' style={{ wordBreak: 'break-word', textAlign: 'start' }} marginBottom='m'>
      <Typography variant='bodyMediumS' color='text-base' as='span'>
        <Flex spaceX='xxxs' onClick={toggleContent} marginBottom='xs' data-test='toggle-button'>
          <Typography variant='bodySemiboldM' color='text-base' as='span' margin='0'>
            {t('number_details.integrations.whatsapp_integration.how_to_proceed')}
          </Typography>
          {isOpen ? <ChevronDownOutlined size={20} /> : <ChevronRightOutlined size={20} />}
        </Flex>
        {isOpen && (
          <>
            <Typography
              variant='bodyMediumS'
              color='text-base'
              as='span'
              data-test='not-registered-content'
            >
              <Trans
                i18nKey='number_details.integrations.whatsapp_integration.how_to_proceed_subtitle'
                components={{
                  customLink: (
                    <Link
                      color='inherit'
                      fontSize='inherit'
                      target='_blank'
                      href={t(
                        'number_details.integrations.whatsapp_integration.how_to_proceed_subtitle_meta_business_portfolio_link'
                      )}
                    />
                  ),
                }}
              />
            </Typography>
            <StyledRegistrationInfo>
              <ol>
                <li>
                  <Typography variant='bodySemiboldM'>
                    {t(
                      'number_details.integrations.whatsapp_integration.status.not_registered.step_1.title'
                    )}
                  </Typography>
                  <Typography variant='bodyMediumS' marginTop='xxs'>
                    {t(
                      'number_details.integrations.whatsapp_integration.status.not_registered.step_1.subtitle'
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant='bodySemiboldM' marginTop='s'>
                    {t(
                      'number_details.integrations.whatsapp_integration.status.not_registered.step_2.title'
                    )}
                  </Typography>
                  <Typography variant='bodyMediumS' marginTop='xxs'>
                    {t(
                      'number_details.integrations.whatsapp_integration.status.not_registered.step_2.subtitle'
                    )}
                  </Typography>
                </li>
                <li>
                  {wabaId ? (
                    <>
                      <Typography variant='bodySemiboldM' marginTop='s'>
                        {t(
                          'number_details.integrations.whatsapp_integration.status.not_registered.step_3.title_waba_id'
                        )}
                      </Typography>

                      <Typography variant='bodyMediumS' marginTop='xxs'>
                        <Trans
                          i18nKey='number_details.integrations.whatsapp_integration.status.not_registered.step_3.subtitle_waba_id'
                          values={{
                            wabaId,
                          }}
                          components={{
                            customLink: (
                              <Link
                                href={t(
                                  'number_details.integrations.whatsapp_integration.meta_info.whatsapp_business_account_link',
                                  {
                                    wabaId,
                                  }
                                )}
                                target='blank'
                              />
                            ),
                          }}
                        />
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant='bodySemiboldM' marginTop='s'>
                        {t(
                          'number_details.integrations.whatsapp_integration.status.not_registered.step_3.title_no_waba_id'
                        )}
                      </Typography>
                      <Typography variant='bodyMediumS' marginTop='xxs'>
                        <Trans
                          i18nKey='number_details.integrations.whatsapp_integration.status.not_registered.step_3.subtitle_no_waba_id'
                          components={{
                            customLink: (
                              <Link
                                color='inherit'
                                fontSize='inherit'
                                target='_blank'
                                href={t(
                                  'number_details.integrations.whatsapp_integration.status.not_registered.step_3.guidelines_link'
                                )}
                              />
                            ),
                          }}
                        />
                      </Typography>
                    </>
                  )}
                </li>
                <li>
                  <Typography variant='bodySemiboldM' marginTop='s'>
                    {t(
                      'number_details.integrations.whatsapp_integration.status.not_registered.step_4.title'
                    )}
                  </Typography>
                  <Typography variant='bodyMediumS' marginTop='xxs'>
                    <Trans
                      i18nKey='number_details.integrations.whatsapp_integration.status.not_registered.step_4.subtitle'
                      values={{
                        numberFormatted,
                      }}
                      components={{
                        bold: <Typography variant='bodySemiboldM' as='span' />,
                      }}
                    />
                  </Typography>
                </li>
                <li>
                  <Typography variant='bodySemiboldM' marginTop='s'>
                    {t(
                      'number_details.integrations.whatsapp_integration.status.not_registered.step_5.title'
                    )}
                  </Typography>
                  <Typography variant='bodyMediumS' marginTop='xxs'>
                    <ul>
                      <li>
                        {t(
                          'number_details.integrations.whatsapp_integration.status.not_registered.step_5.subtitle.line_1'
                        )}
                      </li>
                      <li>
                        {t(
                          'number_details.integrations.whatsapp_integration.status.not_registered.step_5.subtitle.line_2'
                        )}
                      </li>
                    </ul>
                  </Typography>
                </li>
              </ol>
            </StyledRegistrationInfo>
          </>
        )}
      </Typography>
    </Box>
  );
}

function CreatingStatus({
  whatsappRegistrationCreatedAt,
  formattedUpdatedAt,
  onRefreshWhatsappStatus,
}: Readonly<CreatingStatusProps>) {
  const { t } = useTranslation();
  return (
    <>
      <Flex
        flexDirection='column'
        border='1px solid #eee'
        borderRadius={20}
        gap='m'
        alignItems='center'
        padding='xxl'
      >
        <Icon component={InformationOutlined} size={45} color='text-interactive-warning' />
        <Typography variant='bodySemiboldM'>
          {t('number_details.integrations.whatsapp_integration.status.pending.title')}
        </Typography>
        <Typography variant='bodyMediumS'>
          {t('number_details.integrations.whatsapp_integration.status.pending.subtitle')}
        </Typography>
        {whatsappRegistrationCreatedAt && (
          <Button
            onClick={() => onRefreshWhatsappStatus()}
            data-test='refresh-button'
            variant='secondary'
          >
            <RefreshOutlined />
            {t(
              'number_details.integrations.whatsapp_integration.status.pending.refresh_button_text'
            )}
          </Button>
        )}
      </Flex>
      <Flex justifyContent='space-between' mt='s'>
        <Flex flex={2}>
          <Typography variant='bodyRegularS'>
            {t(
              `number_details.integrations.whatsapp_integration.status.pending.status_update_info`
            )}
          </Typography>
        </Flex>
        <Flex flex={1} justifyContent='flex-end'>
          <Typography variant='bodyRegularS'>
            {t(
              `number_details.integrations.whatsapp_integration.status.pending.last_updated_time`,
              {
                updatedAt: formattedUpdatedAt,
              }
            )}
          </Typography>
        </Flex>
      </Flex>
    </>
  );
}

function RegisteredStatus({ metaInfoList }: Readonly<RegisteredStatusProps>) {
  const { t } = useTranslation();

  return (
    <Typography variant='bodyMediumS' color='text-base' as='p' margin='0'>
      <Typography variant='headingBoldS' color='text-base' as='p' margin='0' paddingBottom='s'>
        {t('number_details.integrations.whatsapp_integration.meta_info.title')}
      </Typography>
      <List fluid>
        <ListView>
          {metaInfoList.map((item) => (
            <ListItem key={item.id} data-test={`meta-info-list-item-${item.id}`}>
              <StyledListItem padding='10px' backgroundColor='surface-background'>
                <Flex alignItems='flex-start'>
                  <Box
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '30%' }}
                    data-test={`meta-info-key-${item.id}`}
                  >
                    {item.key}
                  </Box>
                  <Box style={{ width: '70%' }} data-test={`meta-info-value-${item.id}`}>
                    {item.value}
                  </Box>
                </Flex>
              </StyledListItem>
            </ListItem>
          ))}
        </ListView>
      </List>
    </Typography>
  );
}

function ValidationStatusValue({
  status,
  offlineReasons,
  additionalInfo,
}: Readonly<ValidationStatusValueProps>) {
  const { t } = useTranslation();

  return (
    <Box spaceY='xs' margin='0'>
      {status === WhatsappRegistrationState.ONLINE ? (
        <Tag data-test='validation-status-online' size='small' variant='green'>
          {t('number_details.integrations.whatsapp_integration.status.verified.tag')}
        </Tag>
      ) : (
        <Tag data-test='validation-status-offline' size='small' variant='darkRed'>
          {t('number_details.integrations.whatsapp_integration.status.rejected.tag')}
        </Tag>
      )}
      <Box ml='xs' margin='0'>
        <StyledUl as='ul' color='text-interactive-critical' data-test='offline-reasons-list'>
          {offlineReasons?.map((reason) => (
            <li
              key={`offline-reason-${reason?.errorKey}`}
              data-test={`offline-reason-${reason?.errorKey}`}
            >
              <Typography variant='bodyRegularS' color='text-interactive-critical'>
                {reason?.errorKey ? (
                  <Trans
                    i18nKey={`number_details.integrations.whatsapp_integration.meta_info.offline_reason.${reason?.errorKey?.toLowerCase()}`}
                    components={{
                      customLink: (
                        <Link
                          data-test={`offline-reason-link-${reason?.errorKey}`}
                          color='inherit'
                          fontSize='inherit'
                          target='_blank'
                          href={t(
                            `number_details.integrations.whatsapp_integration.meta_info.offline_reason.${reason?.errorKey?.toLowerCase()}_link`
                          )}
                        />
                      ),
                    }}
                  />
                ) : (
                  reason?.description
                )}
              </Typography>
            </li>
          ))}
        </StyledUl>
        <StyledUl
          as='ul'
          color='surface-interactive-neutral-active'
          data-test='additional-info-list'
        >
          {additionalInfo?.map((reason) => (
            <li key={`additional-info-${reason?.key}`} data-test={`additional-info-${reason?.key}`}>
              <Typography variant='bodyRegularS' color='surface-interactive-neutral-active'>
                {reason?.key ? (
                  <Trans
                    i18nKey={`number_details.integrations.whatsapp_integration.meta_info.additional_info.${reason?.key?.toLowerCase()}`}
                    components={{
                      customLink: (
                        <Link
                          data-test={`additional-info-link-${reason?.key}`}
                          color='inherit'
                          fontSize='inherit'
                          target='_blank'
                          href={t(
                            `number_details.integrations.whatsapp_integration.meta_info.additional_info.${reason?.key?.toLowerCase()}_link`
                          )}
                        />
                      ),
                    }}
                  />
                ) : (
                  reason?.value
                )}
              </Typography>
            </li>
          ))}
        </StyledUl>
      </Box>
    </Box>
  );
}

const statusConfig = {
  [SendMessageStatus.AVAILABLE]: { variant: 'green' },
  [SendMessageStatus.BLOCKED]: { variant: 'darkRed' },
  [SendMessageStatus.LIMITED]: { variant: 'darkYellow' },
};

export function NumberDetailWhatsAppStatusIntegrationSection({
  whatsappStatusInfo,
  onRefreshWhatsappStatus,
  numberFormatted,
}: Readonly<NumberDetailWhatsAppStatusIntegrationSectionProps>) {
  const { t } = useTranslation();
  const whatsappRegistrationCreatedAt = whatsappStatusInfo.createdAt
    ? (Date.now() - new Date(whatsappStatusInfo.createdAt).getTime()) / (1000 * 60) > 15
    : false;

  const updatedAtDate = whatsappStatusInfo.updatedAt
    ? new Date(whatsappStatusInfo.updatedAt)
    : null;
  const formattedUpdatedAt = updatedAtDate ? updatedAtDate.toLocaleString() : '-';
  const additionalInfo = whatsappStatusInfo.phoneNumber?.additionalInfo ?? [];
  const filteredAdditionalInfo = additionalInfo.filter(
    (info) => info?.key !== 'DISPLAY_NAME_PENDING'
  );
  const displayNamePendingInfo = additionalInfo.find(
    (info) => info?.key === 'DISPLAY_NAME_PENDING'
  );

  const metaInfoList = [
    {
      id: 1,
      key: t('number_details.integrations.whatsapp_integration.meta_info.connection_status'),
      value: (
        <ValidationStatusValue
          status={whatsappStatusInfo.status}
          offlineReasons={whatsappStatusInfo.phoneNumber?.offlineReasons}
          additionalInfo={filteredAdditionalInfo}
        />
      ),
    },
    {
      id: 2,
      key: t('number_details.integrations.whatsapp_integration.meta_info.account_status.title'),
      value: whatsappStatusInfo?.businessVerificationStatus
        ? t(
            `number_details.integrations.whatsapp_integration.meta_info.account_status.${whatsappStatusInfo.businessVerificationStatus?.toLowerCase()}`
          )
        : '-',
    },
    {
      id: 3,
      key: t('number_details.integrations.whatsapp_integration.meta_info.rate_limit.label'),
      value: (
        <Box spaceY='xs' margin='0'>
          {whatsappStatusInfo.phoneNumber?.messagingLimitTier ? (
            <Typography>
              {t(
                `number_details.integrations.whatsapp_integration.meta_info.rate_limit.${whatsappStatusInfo.phoneNumber?.messagingLimitTier?.toLowerCase()}`
              )}
            </Typography>
          ) : (
            '-'
          )}
          {displayNamePendingInfo && (
            <Typography variant='bodyRegularS' color='surface-interactive-neutral-active'>
              <Trans
                i18nKey='number_details.integrations.whatsapp_integration.meta_info.additional_info.display_name_pending'
                components={{
                  customLink: (
                    <Link
                      color='inherit'
                      fontSize='inherit'
                      target='_blank'
                      href={t(
                        'number_details.integrations.whatsapp_integration.meta_info.additional_info.display_name_pending_link'
                      )}
                    />
                  ),
                }}
              />
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 4,
      key: t('number_details.integrations.whatsapp_integration.meta_info.can_send_message.title'),
      value: (() => {
        const status = whatsappStatusInfo.phoneNumber?.healthStatus?.canSendMessage;
        if (!status) {
          return '-';
        }

        return (
          <Tag
            data-test={`can-send-message-${status.toLowerCase()}`}
            size='small'
            variant={statusConfig[status]?.variant as TagVariants}
          >
            {t(
              `number_details.integrations.whatsapp_integration.meta_info.can_send_message.${status.toLowerCase()}`
            )}
          </Tag>
        );
      })(),
    },
    {
      id: 5,
      key: t(
        'number_details.integrations.whatsapp_integration.meta_info.display_name_status.title'
      ),
      value: whatsappStatusInfo?.phoneNumber?.nameStatus
        ? t(
            `number_details.integrations.whatsapp_integration.meta_info.display_name_status.${whatsappStatusInfo.phoneNumber?.nameStatus?.toLowerCase()}`
          )
        : '-',
    },
    {
      id: 6,
      key: t(
        'number_details.integrations.whatsapp_integration.meta_info.whatsapp_business_account_id'
      ),
      value: whatsappStatusInfo.wabaId ? (
        <Link
          href={t(
            'number_details.integrations.whatsapp_integration.meta_info.whatsapp_business_account_link',
            {
              wabaId: whatsappStatusInfo.wabaId,
            }
          )}
          target='blank'
        >
          <Flex gap='xxxs'>
            {whatsappStatusInfo.wabaId}
            <OutboundOutlined style={{ paddingBottom: '5px' }} />
          </Flex>
        </Link>
      ) : (
        '-'
      ),
    },
  ];

  switch (whatsappStatusInfo.status) {
    case WhatsappRegistrationState.NOT_REGISTERED:
      return (
        <NotRegisteredStatus numberFormatted={numberFormatted} wabaId={whatsappStatusInfo.wabaId} />
      );
    case WhatsappRegistrationState.CREATING:
      return (
        <CreatingStatus
          whatsappRegistrationCreatedAt={whatsappRegistrationCreatedAt}
          formattedUpdatedAt={formattedUpdatedAt}
          onRefreshWhatsappStatus={onRefreshWhatsappStatus}
        />
      );
    case WhatsappRegistrationState.ONLINE:
    case WhatsappRegistrationState.OFFLINE:
      return <RegisteredStatus metaInfoList={metaInfoList} />;
    default:
      return null;
  }
}
